import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Shield, Play, Calendar, Settings, Clock } from 'lucide-react';
import api, { type ManusReview, type CadenceSettings } from '../api';

interface Props {
  projectId: string;
  onOpenArtifact?: (filename: string) => void;
}

const SCORE_COLORS: Record<string, string> = {
  excellent: '#22c55e',
  good: '#3b82f6',
  fair: '#eab308',
  poor: '#00E5FF',
  critical: '#ef4444',
};

function scoreRating(score: number): string {
  if (score >= 4.5) return 'excellent';
  if (score >= 3.5) return 'good';
  if (score >= 2.5) return 'fair';
  if (score >= 1.5) return 'poor';
  return 'critical';
}

function scoreColor(score: number): string {
  return SCORE_COLORS[scoreRating(score)] ?? 'var(--text-muted)';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function ReviewsPanel({ projectId, onOpenArtifact }: Props) {
  const [reviews, setReviews] = useState<ManusReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);

  // Cadence settings state
  const [cadence, setCadence] = useState<CadenceSettings | null>(null);
  const [cadenceEnabled, setCadenceEnabled] = useState(false);
  const [cadenceWeeks, setCadenceWeeks] = useState(3);
  const [cadenceSaving, setCadenceSaving] = useState(false);

  const loadReviews = useCallback(() => {
    setLoading(true);
    api.manusReviews(projectId)
      .then(setReviews)
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  // Load cadence settings
  useEffect(() => {
    api.manusCadence(projectId)
      .then((c: CadenceSettings) => {
        setCadence(c);
        setCadenceEnabled(c.review_cadence_enabled);
        setCadenceWeeks(c.review_cadence_weeks);
      })
      .catch(() => {});
  }, [projectId]);

  const handleRunNow = async () => {
    setDispatching(true);
    try {
      // Use the existing chat endpoint to trigger deep review
      await api.chatV2Send('Review deep with Manus', projectId);
    } catch {
      // Non-fatal — user will see the result in chat
    } finally {
      setDispatching(false);
    }
  };

  // Build chart data from reviews (oldest first)
  const chartData = [...reviews]
    .reverse()
    .filter((r: ManusReview) => r.review_score != null)
    .map((r: ManusReview) => ({
      date: formatDate(r.created_at),
      score: parseFloat(r.review_score ?? '0'),
    }));

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading reviews...
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      {/* Header with Run Now button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={16} style={{ color: 'var(--blue)' }} />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
            Architectural Reviews
          </span>
        </div>
        <button
          className="btn btn-sm"
          onClick={handleRunNow}
          disabled={dispatching}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'var(--blue)', color: '#fff', border: 'none',
            opacity: dispatching ? 0.6 : 1,
          }}
        >
          <Play size={12} />
          {dispatching ? 'Starting...' : 'Run Deep Review Now'}
        </button>
      </div>

      {/* Score trend chart */}
      {chartData.length > 1 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Score Trend
          </div>
          <div style={{ height: 120, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={24} />
                <Tooltip
                  contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: 'var(--text-muted)' }}
                />
                <Line type="monotone" dataKey="score" stroke="var(--blue)" strokeWidth={2} dot={{ r: 3, fill: 'var(--blue)' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            No deep reviews yet. Click "Run Deep Review Now" to start your first one.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reviews.map((review: ManusReview) => {
            const score = review.review_score ? parseFloat(review.review_score) : null;
            const p0 = review.p0_count ?? 0;
            const p1 = review.p1_count ?? 0;
            const p2 = review.p2_count ?? 0;
            const riskAreas = (review.risk_areas_json as string[] | null) ?? [];

            return (
              <div
                key={review.id}
                className="card"
                style={{ padding: '12px 16px', cursor: 'pointer' }}
                onClick={() => onOpenArtifact?.('deep-review.md')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Date */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}>
                    <Calendar size={12} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                      {formatDate(review.created_at)}
                    </span>
                  </div>

                  {/* Score */}
                  {score != null && (
                    <span style={{
                      fontSize: 'var(--text-sm)', fontWeight: 700,
                      color: scoreColor(score), fontFamily: 'var(--font-mono)',
                      minWidth: 36, textAlign: 'center',
                    }}>
                      {score.toFixed(1)}
                    </span>
                  )}

                  {/* Priority counts */}
                  <div style={{ display: 'flex', gap: 8, fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)' }}>
                    {p0 > 0 && <span style={{ color: '#ef4444' }}>P0:{p0}</span>}
                    {p1 > 0 && <span style={{ color: '#00E5FF' }}>P1:{p1}</span>}
                    {p2 > 0 && <span style={{ color: '#eab308' }}>P2:{p2}</span>}
                    {p0 === 0 && p1 === 0 && p2 === 0 && (
                      <span style={{ color: 'var(--text-muted)' }}>No findings</span>
                    )}
                  </div>

                  {/* Risk areas */}
                  <div style={{ flex: 1, display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {riskAreas.slice(0, 3).map((area: string, i: number) => (
                      <span key={i} style={{
                        fontSize: 10, padding: '1px 6px',
                        background: 'rgba(59,130,246,0.08)', color: 'var(--blue)',
                        borderRadius: 4, whiteSpace: 'nowrap',
                      }}>
                        {area}
                      </span>
                    ))}
                  </div>

                  {/* Status */}
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 10,
                    background: review.status === 'completed' ? 'rgba(34,197,94,0.1)' : 'rgba(249,115,22,0.1)',
                    color: review.status === 'completed' ? '#22c55e' : '#00E5FF',
                    fontWeight: 600,
                  }}>
                    {review.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cadence Settings */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 24 }}>
        <div style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Settings size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
            Architectural Review Cadence
          </span>
        </div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={cadenceEnabled}
              onChange={(e) => setCadenceEnabled(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--blue)' }}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
              Enable scheduled deep reviews
            </span>
          </label>

          {/* Interval */}
          {cadenceEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)' }}>Run every</span>
              <input
                type="number"
                min={1}
                max={12}
                value={cadenceWeeks}
                onChange={(e) => setCadenceWeeks(Math.max(1, Math.min(12, parseInt(e.target.value) || 3)))}
                style={{
                  width: 56, padding: '4px 8px', fontSize: 'var(--text-sm)',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
                  fontFamily: 'var(--font-mono)', textAlign: 'center',
                }}
              />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)' }}>weeks</span>
            </div>
          )}

          {/* Schedule info */}
          {cadence && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {cadence.review_next_scheduled_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  <Clock size={11} />
                  Next review scheduled: {formatDate(cadence.review_next_scheduled_at)}
                </div>
              )}
              {cadence.review_last_scheduled_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  <Clock size={11} />
                  Last scheduled review: {formatDate(cadence.review_last_scheduled_at)}
                </div>
              )}
            </div>
          )}

          {/* Save */}
          <button
            className="btn btn-sm"
            disabled={cadenceSaving}
            onClick={async () => {
              setCadenceSaving(true);
              try {
                await api.manusCadenceUpdate(projectId, {
                  review_cadence_enabled: cadenceEnabled,
                  review_cadence_weeks: cadenceWeeks,
                });
                // Refresh cadence state
                const updated = await api.manusCadence(projectId);
                setCadence(updated);
              } catch {
                // Non-fatal
              } finally {
                setCadenceSaving(false);
              }
            }}
            style={{
              alignSelf: 'flex-start',
              background: 'var(--blue)', color: '#fff', border: 'none',
              opacity: cadenceSaving ? 0.6 : 1,
            }}
          >
            {cadenceSaving ? 'Saving...' : 'Save Cadence Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
